import * as React from 'react';
import { useCallback } from 'react';
import { SvgWrapper } from 'src/components/SvgWrapper';
import { ReactComponent as ArrowIcon } from 'src/assets/icon_arrow_link.svg';
import { ReactComponent as UnlinkIcon } from 'src/assets/icon_unlink.svg';
import { ReactComponent as UserIcon } from 'src/assets/icon_user.svg';
import { ClassNameMap } from '@mui/styles';
import { IBitrixDeal, IBitrixDealPlacement, IBitrixDealsInDealPlacement } from 'src/store/crosstable/reducer';
import Box from '@mui/material/Box';
import { useBitrixDealHook } from 'src/components/ApartmentConnectDeal/hooks/useBitrixDealHook';
import { useSelector } from 'react-redux';
import { getCurrentLang } from 'src/store/app/selectors';
import { useBitrixPlacementDeal } from 'src/components/ApartmentConnectDeal/hooks/useBitrixPlacementDeal';

interface Props {
  title: string;
  placementDeal?: IBitrixDealPlacement;
  otherDeal?: IBitrixDealsInDealPlacement;
  styles: ClassNameMap;
  bitrixDeal: IBitrixDeal | null;
  onUnlink?: (dealId: string) => void;
}
export const DealDetails: React.FC<Props> = React.memo(({
  styles, title, placementDeal, bitrixDeal, otherDeal, onUnlink,
}) => {
  const _deal = otherDeal || bitrixDeal;
  const {
    getTimeAgoContent, getLinkedDateContent, isLinkedUnit, openCurrentDealLink,
  } = useBitrixDealHook(bitrixDeal, otherDeal);
  const { isUnitCanBeUnLinked } = useBitrixPlacementDeal(_deal);
  const timeAgoContent = getTimeAgoContent(_deal?.virtual);
  const linkedDateContent = getLinkedDateContent(_deal?.virtual);
  const currentLang = useSelector(getCurrentLang);
  const _isLinkedUnit = isLinkedUnit(_deal);
  const dealPlacementNumeration: string = placementDeal?.numeration?.[currentLang] || bitrixDeal?.placement?.numeration?.[currentLang] || '';
  const linedPlacementStatusColor: string = placementDeal?.placementStatus?.crosstableColor || bitrixDeal?.placement?.placementStatus?.crosstableColor || '';
  const onUnlinkLocal = useCallback(() => {
    if (onUnlink) {
      const unlinkDealID = otherDeal?.id || bitrixDeal?.id;
      if (unlinkDealID) onUnlink(unlinkDealID);
    }
  }, [onUnlink, otherDeal, bitrixDeal]);

  return (
    <div className={styles.dealDetailsWrapper}>
      <div className={styles.dealMainInfo}>
        <div className="main">
          <div className="dealStatus">{ title }</div>
          {_deal?.bitrixExternalDealId && (
            <div className="dealNumber">{`#${_deal.bitrixExternalDealId}`}</div>
          )}
        </div>
        {_deal?.bitrixExternalUrl && isLinkedUnit && (
          <Box className="dealLink circle" onClick={() => openCurrentDealLink(_deal.bitrixExternalUrl)}>
            <SvgWrapper size={16}>
              <ArrowIcon />
            </SvgWrapper>
          </Box>
        ) }
      </div>
      <div className={styles.dealAdditionalInfo}>
        {_deal?.bitrixExternalName && (
          <div className="dealName">{_deal.bitrixExternalName}</div>
        )}
        {_isLinkedUnit && _deal?.virtual?.lastPlacementLink && (
          <div className="dealLinkedBy">
            {_deal?.virtual?.lastPlacementLink?.by?.username && (
              <div className="dealLinkedByName">
                <span className="badge">
                  Linked by
                  { ' ' }
                </span>
                <span className="name">{ _deal.virtual.lastPlacementLink.by.username }</span>
              </div>
            ) }
            {(linkedDateContent || timeAgoContent) && (
              <div className="dealLinkedByDate">
                <div className="date">{ linkedDateContent || '' }</div>
                <div className="timeAgo">{ timeAgoContent || '' }</div>
              </div>
            )}
          </div>
        ) }
        <div className="contacts">
          {(_deal?.bitrixDealClient?.name || _deal?.bitrixDealClient?.phones) && (
            <div className="avatar circle">
              <SvgWrapper size={28}>
                <UserIcon />
              </SvgWrapper>
            </div>
          ) }
          <div className="details">
            {_deal?.bitrixDealClient?.name && (
              <div className="name">{_deal.bitrixDealClient.name}</div>
            )}
            {_deal?.bitrixDealClient?.phones?.length && (
              <div className="phone">{_deal.bitrixDealClient?.phones.join(', ')}</div>
            ) }
          </div>
        </div>
        <div className="linked">
          <div className="unit">
            {_isLinkedUnit ? (
              <div className="unit linked-unit">
                <div className="status circle" style={{ backgroundColor: linedPlacementStatusColor }} />
                <div className="unit-text">Linked unit</div>
                <div className="unit-number">{ `#${dealPlacementNumeration}` }</div>
              </div>
            ) : (
              <div className="unit no-linked-unit">
                <div className="status circle" />
                <div className="unit-text">No linked unit yet</div>
              </div>
            )}
          </div>
          {_isLinkedUnit && (
            <div className="unlink">
              { isUnitCanBeUnLinked && (
                <>
                  <div className="icon">
                    <SvgWrapper size={8}>
                      <UnlinkIcon />
                    </SvgWrapper>
                  </div>
                  <Box className="href" onClick={onUnlinkLocal}>Unlink</Box>
                </>
              ) }
            </div>
          ) }
        </div>
      </div>
    </div>
  );
});
